import { useState, useEffect, useMemo } from "react";

const API = "https://web-production-61310.up.railway.app";
const ORANGE = "#FF6B00";
const DARK = "#0d0d0d";
const CARD_BG = "#161616";
const CARD_BORDER = "#222";
const TEXT_DIM = "#555";
const TEXT_MID = "#888";

function fmt(n, currency = "ARS") {
  if (n == null || isNaN(n)) return "—";
  return (currency === "USD" ? "USD " : "$") + Math.round(n).toLocaleString(currency === "USD" ? "en-US" : "es-AR");
}

function fmtK(n, currency = "ARS") {
  if (n == null) return "—";
  const sym = currency === "USD" ? "USD " : "$";
  if (n >= 1_000_000) return sym + (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return sym + (n / 1_000).toFixed(0) + "K";
  return fmt(n, currency);
}

function pct(v) {
  if (v == null) return "—";
  return (v >= 0 ? "+" : "") + v.toFixed(1) + "%";
}

function Sparkline({ data, currency }) {
  if (!data || !data.length) return null;
  const vals = data.map((d) => d.ventas || 0);
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const range = max - min || 1;
  const pad = 8;
  const W = 560; const H = 70;
  const w = W - pad * 2; const h = H - pad * 2;

  const pts = vals.map((v, i) => {
    const x = pad + (i / Math.max(vals.length - 1, 1)) * w;
    const y = pad + h - ((v - min) / range) * h;
    return [x, y];
  });

  const linePts = pts.map((p) => p.join(",")).join(" ");
  const areaPts = [...pts.map((p) => p.join(",")), `${pad + w},${pad + h}`, `${pad},${pad + h}`].join(" ");

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, display: "block" }}>
      <defs>
        <linearGradient id="spGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={ORANGE} stopOpacity="0.15" />
          <stop offset="100%" stopColor={ORANGE} stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <polygon points={areaPts} fill="url(#spGrad)" />
      <polyline points={linePts} fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3" fill={CARD_BG} stroke={ORANGE} strokeWidth="1.5" />
      ))}
    </svg>
  );
}

function MetricCard({ label, value, sub, change, isInflation }) {
  return (
    <div style={{
      background: isInflation ? "#1a1200" : CARD_BG,
      border: `1px solid ${isInflation ? "#3a2800" : CARD_BORDER}`,
      borderRadius: 12, padding: "14px 16px",
    }}>
      <div style={{ fontSize: 10, color: TEXT_MID, fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{
        fontSize: isInflation ? 15 : 19, fontWeight: 700,
        fontFamily: isInflation ? "inherit" : "'DM Mono', monospace",
        color: isInflation ? "#f0a020" : "#fff", letterSpacing: "-0.3px",
      }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: TEXT_DIM, marginTop: 5 }}>{sub}</div>
      {!isInflation && change != null && (
        <div style={{ fontSize: 11, marginTop: 6, fontWeight: 600, color: change >= 0 ? "#5a9e3a" : "#cc4444" }}>
          {change >= 0 ? "↑" : "↓"} {pct(Math.abs(change))} vs. sem. ant.
        </div>
      )}
    </div>
  );
}

function ClimaCell({ clima, tempUnit }) {
  if (!clima) return <span style={{ fontSize: 11, color: "#333" }}>—</span>;
  const icons = { sol: "☀️", parcial: "⛅", lluvia: "🌧️", tormenta: "⛈️" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
      <span style={{ fontSize: 17 }}>{icons[clima.icono] || "🌡️"}</span>
      <span style={{ color: TEXT_MID }}>
        {clima.temp_max}°/{clima.temp_min}°
      </span>
    </div>
  );
}

function Dot({ color }) {
  return <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: color, marginRight: 5, verticalAlign: "middle" }} />;
}

const PERIODS = [
  { label: "7 días", dias: 7 },
  { label: "14 días", dias: 14 },
  { label: "28 días", dias: 28 },
];

export default function SalesProjection({ slug, currency = "ARS" }) {
  const [periodIdx, setPeriodIdx] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animate, setAnimate] = useState(false);

  const period = PERIODS[periodIdx];

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API}/${slug}/proyeccion?dias=${period.dias}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => { setData(d); setLoading(false); setAnimate(true); setTimeout(() => setAnimate(false), 600); })
      .catch((e) => { setError(e.message); setLoading(false); });
  }, [slug, period.dias]);

  const cur = data?.moneda || currency;
  const tempUnit = cur === "USD" ? "°F" : "°C";
  const dias = data?.dias || [];

  const CSS = `
    @keyframes fadeRow { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
    .proj-tab{background:none;border:1px solid;cursor:pointer;font-family:'DM Sans',sans-serif;font-size:12px;font-weight:500;padding:6px 14px;border-radius:8px;transition:all .2s}
    .proj-tab.active{background:#fff;color:#000;border-color:#fff}
    .proj-tab.inactive{color:#555;border-color:#2a2a2a}
    .proj-tab.inactive:hover{border-color:#444;color:#aaa}
    .proj-tr td{padding:11px 13px;vertical-align:top;border-bottom:1px solid #1a1a1a}
    .proj-tr:last-child td{border-bottom:none}
  `;

  if (loading) return (
    <div style={{ padding: "40px 0", textAlign: "center", color: TEXT_MID }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>Calculando proyección...</div>
      <div style={{ fontSize: 11, color: TEXT_DIM }}>Analizando 12 meses de historial</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: "40px 0", textAlign: "center", color: "#cc4444" }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>Error al cargar proyección</div>
      <div style={{ fontSize: 11, color: TEXT_DIM }}>{error}</div>
    </div>
  );

  if (!data) return null;

  const t = data.totales;

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", color: "#fff" }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>Proyección de ventas</span>
          <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 7px", borderRadius: 5, background: "rgba(255,107,0,.15)", color: ORANGE, letterSpacing: "0.8px" }}>BETA</span>
        </div>
        <div style={{ fontSize: 10, color: TEXT_DIM, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: "'DM Mono', monospace", background: "#161616", border: "1px solid #222", padding: "3px 8px", borderRadius: 5 }}>
            {data.fuente}
          </span>
          <span>⟳ {new Date(data.generado_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
      </div>

      {/* Period selector */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
        {PERIODS.map((p, i) => (
          <button key={p.dias} className={`proj-tab ${i === periodIdx ? "active" : "inactive"}`}
            onClick={() => setPeriodIdx(i)}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Metric cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 18 }}>
        <MetricCard label="Ventas netas" value={fmtK(t.ventas, cur)}
          sub={`${fmtK(t.ventas_lo, cur)} – ${fmtK(t.ventas_hi, cur)}`}
          change={t.cambio_ventas_pct} />
        <MetricCard label="Órdenes" value={(t.ordenes || 0).toLocaleString()}
          sub={`${(t.ordenes_lo || 0).toLocaleString()} – ${(t.ordenes_hi || 0).toLocaleString()}`}
          change={t.cambio_ordenes_pct} />
        <MetricCard label="Ticket promedio" value={fmt(t.ticket_promedio, cur)}
          sub="promedio proyectado" change={null} />
        {cur === "ARS" && (
          <MetricCard label="Ajuste IPC" value={pct(data.inflacion_mensual_pct) + " mens."}
            sub="Pesos corrientes" isInflation />
        )}
      </div>

      {/* Sparkline */}
      <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "12px 16px 8px", marginBottom: 18 }}>
        <div style={{ fontSize: 10, color: TEXT_DIM, fontWeight: 600, letterSpacing: "0.6px", marginBottom: 8 }}>
          TENDENCIA · PRÓXIMOS {period.dias} DÍAS
        </div>
        <Sparkline data={dias} currency={cur} />
      </div>

      {/* Table */}
      <div style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}`, borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              {["Día", "Ventas netas", "Órdenes", "Ticket prom.", "Clima"].map((h, i) => (
                <th key={h} style={{
                  textAlign: "left", padding: "10px 13px", fontWeight: 600,
                  fontSize: 10, color: TEXT_MID, letterSpacing: "0.8px", textTransform: "uppercase",
                  width: i === 0 ? "14%" : i === 1 ? "28%" : i === 2 ? "16%" : i === 3 ? "24%" : "18%",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dias.map((row, i) => (
              <tr key={row.fecha} className="proj-tr" style={{
                opacity: animate ? 0 : 1,
                animation: animate ? `fadeRow 0.35s ease ${i * 25}ms forwards` : "none",
              }}>
                {/* Día */}
                <td>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#fff" }}>
                    {row.dia_semana} {parseInt(row.fecha.slice(8))}
                  </div>
                  {row.es_hoy && (
                    <span style={{ display: "inline-block", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(255,107,0,.15)", color: ORANGE, marginTop: 3 }}>
                      HOY
                    </span>
                  )}
                  {row.es_manana && (
                    <span style={{ display: "inline-block", fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "#1e1e1e", color: TEXT_MID, marginTop: 3 }}>
                      MAÑANA
                    </span>
                  )}
                </td>
                {/* Ventas */}
                <td>
                  <div style={{ fontWeight: 700, fontSize: 13, fontFamily: "'DM Mono', monospace", color: "#fff" }}>
                    {fmt(row.ventas, cur)}
                  </div>
                  <div style={{ fontSize: 10, color: "#2e2e2e", marginTop: 2 }}>
                    {fmt(row.ventas_lo, cur)} – {fmt(row.ventas_hi, cur)}
                  </div>
                  {row.inflacion_aplicada && data.pais === "AR" && (
                    <span style={{ display: "inline-block", fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "#1a1200", color: "#f0a020", fontWeight: 600, marginTop: 3 }}>
                      ↑ {pct(data.inflacion_mensual_pct)} IPC
                    </span>
                  )}
                </td>
                {/* Órdenes */}
                <td>
                  <div style={{ fontWeight: 600, color: "#fff" }}>{row.ordenes}</div>
                  <div style={{ fontSize: 10, color: "#2e2e2e", marginTop: 2 }}>
                    {row.ordenes_lo} – {row.ordenes_hi}
                  </div>
                </td>
                {/* Ticket */}
                <td>
                  <div style={{ fontWeight: 600, fontFamily: "'DM Mono', monospace", color: "#fff" }}>
                    {fmt(row.ticket_promedio, cur)}
                  </div>
                  <div style={{ fontSize: 10, color: "#2e2e2e", marginTop: 2 }}>
                    {fmt(row.ticket_lo, cur)} – {fmt(row.ticket_hi, cur)}
                  </div>
                </td>
                {/* Clima */}
                <td>
                  <ClimaCell clima={row.clima} tempUnit={tempUnit} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 10, color: "#444", flexWrap: "wrap" }}>
        <span><Dot color={ORANGE} /> Proyección central</span>
        <span><Dot color="#2e2e2e" /> Rango ±1σ</span>
        {data.pais === "AR" && <span><Dot color="#f0a020" /> Ajuste inflación</span>}
      </div>
    </div>
  );
}
